import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { requestGlobalPushPermission, requestPushPermission } from '../utils/pushManager';
import './NotificationPrompt.css';

const COUNTER_KEY = 'olso-notif-remind-orders';

interface NotificationPromptProps {
  qrCode: string;
  orderId?: string;
}

export default function NotificationPrompt({ qrCode, orderId }: NotificationPromptProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Only in standalone mode
    const standaloneNav = (window.navigator as { standalone?: boolean }).standalone === true;
    const standaloneMedia = window.matchMedia('(display-mode: standalone)').matches;
    if (!standaloneNav && !standaloneMedia) return;

    // Already granted or denied
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') return;
    if (Notification.permission === 'denied') return;

    // Increment counter for this qrCode visit
    const visitedKey = `olso-notif-visited-${qrCode}`;
    if (sessionStorage.getItem(visitedKey)) return; // already counted this session
    sessionStorage.setItem(visitedKey, '1');

    const prev = parseInt(localStorage.getItem(COUNTER_KEY) || '0', 10);
    const next = prev + 1;
    localStorage.setItem(COUNTER_KEY, String(next));

    // Show on first visit (1) or every odd visit (1, 3, 5...) = every 2nd order
    if (next === 1 || next % 2 === 1) {
      setShow(true);
    }
  }, [qrCode]);

  const handleAllow = async () => {
    try {
      const result = await Notification.requestPermission();
      // Close modal immediately — don't wait for FCM token registration
      setShow(false);
      if (result === 'granted') {
        localStorage.removeItem(COUNTER_KEY);
        // Register FCM tokens in background (don't block UI)
        if (orderId) requestPushPermission(orderId).catch(() => {});
        requestGlobalPushPermission().catch(() => {});
      }
    } catch (err) {
      console.error('[NotificationPrompt] permission error:', err);
      setShow(false);
    }
  };

  if (!show) return null;

  return createPortal(
    <div className="notif-prompt-overlay">
      <div className="notif-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="notif-prompt-icon">{'\ud83d\udd14'}</div>
        <div className="notif-prompt-title">
          {'\u0420\u0430\u0437\u0440\u0435\u0448\u0438\u0442\u0435 \u0443\u0432\u0435\u0434\u043e\u043c\u043b\u0435\u043d\u0438\u044f, \u0447\u0442\u043e\u0431\u044b \u0443\u0437\u043d\u0430\u0442\u044c \u043a\u043e\u0433\u0434\u0430 \u0437\u0430\u043a\u0430\u0437 \u0431\u0443\u0434\u0435\u0442 \u0433\u043e\u0442\u043e\u0432'}
        </div>
        <button className="notif-prompt-btn" onClick={handleAllow}>
          {'\u0420\u0430\u0437\u0440\u0435\u0448\u0438\u0442\u044c'}
        </button>
        <button className="notif-prompt-btn-secondary" onClick={() => setShow(false)}>
          {'\u041d\u0435 \u0441\u0435\u0439\u0447\u0430\u0441'}
        </button>
      </div>
    </div>,
    document.body
  );
}
